<template>
  <div class="tag-location-options">
    <template v-if="isMobile && $store.state.mobileView === 'map'">
      <div class="flex h-full items-center">
        <div class="text-2xl px-3 h-full flex items-center" @click="closeMapAndGoToResults">
          <XIcon class="w-7 h-7" />
        </div>
        <TagLocationFiltersSmall
          v-bind="filtersProps"
          @toggleOpen="toggleOpen"
          @locationChange="locationChange"
          @eventTypeChange="eventTypeChange"
          @submit="submit"
          @desktopOpenChange="desktopOpen = $event"
        />
        <MoreOptions
          class="search-filters__tab search-filters__tab-more"
          :is-mobile="true"
          data-test-id="more-search-filter"
        />
      </div>
    </template>
    <template v-else>
      <teleport to="#ds-header__actions">
        <div class="flex h-full items-center" :class="{ 'tag-location-options--highlighted': eventTypeHighlighted }">
          <LogoShort
            :homepage-url="$store.state.routes.home"
          />
          <TagLocationFiltersBig
            v-if="!isMobile"
            v-bind="filtersProps"
            @locationChange="locationChange"
            @eventTypeChange="eventTypeChange"
          />
          <TagLocationFiltersSmall
            v-else
            v-bind="filtersProps"
            @toggleOpen="toggleOpen"
            @locationChange="locationChange"
            @eventTypeChange="eventTypeChange"
            @submit="submit"
            @desktopOpenChange="desktopOpen = $event"
          />
        </div>
      </teleport>
    </template>

    <MobilePopover
      v-if="mobileOpen"
      title="What are you planning?"
      no-padding
      lock-background-scroll
      :block-outside-click="eventTypeOpened || locationOpened"
      @close="submit"
    >
      <LocationFilters
        ref="locationFilters"
        class="pb-3 p-4"
        :form="form"
        :tags="tags"
        :is-mobile="isMobile"
        @locationChange="locationChange"
        @eventTypeChange="eventTypeChange"
        @eventTypeOpenChanged="eventTypeOpenChanged"
        @locationOpenChanged="locationOpenChanged"
      />

      <ActionsWrapper>
        <BaseButton
          class="w-full"
          color="green"
          :is-loading="isLoading"
          @click="mobileSubmit"
        >
          {{ submitLabel }}
        </BaseButton>
      </ActionsWrapper>
    </MobilePopover>
  </div>
</template>

<script>
import XIcon from '@tagvenue/icons/24/line/general/x.svg';

import ScopedStyles from '@/components/ScopedStyles.js';
import ActionsWrapper from '@/components/Ui/ActionsWrapper.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import MobilePopover from '@/components/Ui/MobilePopover/MobilePopover.vue';

import MoreOptions from '../More/MoreOptions.vue';
import SearchOptionShared from '../SearchOptionShared.js';
import LocationFilters from './Location/LocationFilters.vue';
import LogoShort from './LogoShort.vue';
import styles from './styles.js';
import TagLocationFiltersBig from './TagLocationFiltersBig.vue';
import TagLocationFiltersSmall from './TagLocationFiltersSmall.vue';

export default {
  name: 'TagLocationOptions',
  mixins: [ScopedStyles, SearchOptionShared],
  components: {
    MoreOptions,
    XIcon,
    ActionsWrapper,
    LogoShort,
    TagLocationFiltersBig,
    TagLocationFiltersSmall,
    LocationFilters,
    MobilePopover,
    BaseButton,
  },
  data() {
    return {
      styles,
      tags: [],
      eventTypeOpened: false,
      locationOpened: false,
      eventTypeHighlighted: this.$store.state.eventTypeSwitchedByChip,
    };
  },
  computed: {
    form() {
      return {
        eventType: this.$store.state.eventTypeSlug,
        location: this.$store.state.filters.location.value,
      };
    },
    eventTypeLabel() {
      return this.$store.state.eventType;
    },
    filtersProps() {
      return {
        isLoading: this.isLoading,
        isMobile: this.isMobile,
        eventTypeLabel: this.eventTypeLabel,
        desktopOpen: this.desktopOpen,
        form: this.form,
        tags: this.tags,
      };
    },
  },
  methods: {
    locationChange(value) {
      this.$store.dispatch('locationChange', value);
      this.trackFilterAppliedAfterRoomsLoaded(value);
    },
    trackFilterAppliedAfterRoomsLoaded(location) {
      const subscriber = $.subscribe('v.search.rooms-loaded', () => {
        this.$store.dispatch('trackGA4Filters', {
          filter: 'Location',
          filterValue: location.value,
        });
        subscriber.unsubscribe();
      });
    },
    eventTypeChange(value) {
      const eventTypeLabel = (this.tags.find(tag => tag.value === value) || {}).label;
      this.$store.dispatch('changeEventType', {
        key: value,
        value: eventTypeLabel,
      });
      this.apply({ name: 'Event', value });
    },
    eventTypeOpenChanged(isOpen) {
      setTimeout(() => {
        this.eventTypeOpened = isOpen;
      }, 500);
    },
    locationOpenChanged(isOpen) {
      setTimeout(() => {
        this.locationOpened = isOpen;
      }, 500);
    },
    mobileSubmit() {
      const { isValid } = this.$refs.locationFilters.validate();
      if (isValid) {
        this.submit();
      }
    },
    closeMapAndGoToResults() {
      this.$store.dispatch('showResults');

      this.$store.dispatch('trackGA4Interaction', {
        section: 'map',
        element: 'close_button',
        interaction: 'click',
      });
    },
  },
  mounted() {
    if (this.eventTypeHighlighted) {
      setTimeout(() => {
        this.eventTypeHighlighted = false;
      }, 1500);
    }
  },
  beforeMount() {
    App.es6.shared.tags_storage().getTags().then(res => {
      this.tags = res.tags.map(tag => ({
        value: tag.data.key,
        label: tag.value,
      }));
    });
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;

// The field's own focus ring – focusing it for real would open the event type dropdown
.tag-location-options--highlighted :deep(.search-field .ds-text-field__input),
.tag-location-options--highlighted :deep(.location-input .ds-text-field__input) {
  box-shadow: 0 0 0 3px rgba(ds-variables.$blue, 0.5);
}
</style>
